import { EgenskapTillattVerdi, Egenskapstype } from '@/domain/vegobjekter/Egenskapstype';
import { EgenskapsfilterVerdi } from '@/middleware/queries/Egenskapsfilter';
import { OperatorAsString, OperatorE } from '@/middleware/queries/Operator';
import { v4 as uuidv4 } from 'uuid';

let nextAdvancedFilterId = 0;

export const ADVANCED_FILTER_MAX_DEPTH = 20;

export type AdvancedLogicalOperator = 'AND' | 'OR';

export interface AdvancedFilterGroupNode {
    readonly id: string;
    readonly kind: 'group';
    readonly negated: boolean;
    readonly operator: AdvancedLogicalOperator;
    readonly children: AdvancedFilterNode[];
}

export interface AdvancedFilterEgenskapNode {
    readonly id: string;
    readonly kind: 'egenskap';
    readonly egenskapstype: Egenskapstype | null;
    readonly operator: OperatorE | null;
    readonly verdi: EgenskapsfilterVerdi | null;
}

export interface AdvancedFilterOverlappNode {
    readonly id: string;
    readonly kind: 'overlapp';
    readonly negated: boolean;
    readonly targetTypeId: number | null;
    readonly child: AdvancedFilterGroupNode | null;
}

export interface AdvancedFilterRelasjonNode {
    readonly id: string;
    readonly kind: 'relasjon';
    readonly negated: boolean;
    readonly targetTypeId: number | null;
    readonly child: AdvancedFilterGroupNode | null;
}

export type AdvancedFilterNode =
    | AdvancedFilterGroupNode
    | AdvancedFilterEgenskapNode
    | AdvancedFilterOverlappNode
    | AdvancedFilterRelasjonNode;

export interface AdvancedFilterHash {
    readonly version: 1;
    readonly root: AdvancedFilterHashGroup;
}

export interface AdvancedFilterHashGroup {
    readonly kind: 'group';
    readonly negated: boolean;
    readonly operator: AdvancedLogicalOperator;
    readonly children: AdvancedFilterHashNode[];
}

export interface AdvancedFilterHashEgenskap {
    readonly kind: 'egenskap';
    readonly type_id: number;
    readonly operator: OperatorAsString;
    readonly verdi: string | number | null;
}

export interface AdvancedFilterHashOverlapp {
    readonly kind: 'overlapp';
    readonly negated: boolean;
    readonly target_type_id: number;
    readonly child: AdvancedFilterHashGroup | null;
}

export interface AdvancedFilterHashRelasjon {
    readonly kind: 'relasjon';
    readonly negated: boolean;
    readonly target_type_id: number;
    readonly child: AdvancedFilterHashGroup | null;
}

export type AdvancedFilterHashNode =
    | AdvancedFilterHashGroup
    | AdvancedFilterHashEgenskap
    | AdvancedFilterHashOverlapp
    | AdvancedFilterHashRelasjon;

export function createAdvancedFilterId(): string {
    const suffix = nextAdvancedFilterId++;
    return `${uuidv4()}-${suffix}`;
}

export function createAdvancedFilterGroup(
    operator: AdvancedLogicalOperator = 'AND',
    children: AdvancedFilterNode[] = [],
    negated = false
): AdvancedFilterGroupNode {
    return {
        id: createAdvancedFilterId(),
        kind: 'group',
        negated,
        operator,
        children,
    };
}

export function createAdvancedFilterRoot(
    children: AdvancedFilterNode[] = [],
    negated = false
): AdvancedFilterGroupNode {
    return createAdvancedFilterGroup('AND', children, negated);
}

export function createAdvancedFilterEgenskapNode(
    egenskapstype: Egenskapstype | null = null,
    operator: OperatorE | null = null,
    verdi: EgenskapsfilterVerdi | null = null
): AdvancedFilterEgenskapNode {
    return {
        id: createAdvancedFilterId(),
        kind: 'egenskap',
        egenskapstype,
        operator,
        verdi,
    };
}

export function createAdvancedFilterOverlappNode(
    targetTypeId: number | null = null,
    child: AdvancedFilterGroupNode | null = null,
    negated = false
): AdvancedFilterOverlappNode {
    return {
        id: createAdvancedFilterId(),
        kind: 'overlapp',
        negated,
        targetTypeId,
        child,
    };
}

export function createAdvancedFilterRelasjonNode(
    targetTypeId: number | null = null,
    child: AdvancedFilterGroupNode | null = null,
    negated = false
): AdvancedFilterRelasjonNode {
    return {
        id: createAdvancedFilterId(),
        kind: 'relasjon',
        negated,
        targetTypeId,
        child,
    };
}

export function isAdvancedFilterGroupNode(
    node: AdvancedFilterNode | null | undefined
): node is AdvancedFilterGroupNode {
    return node?.kind === 'group';
}

export function isAdvancedFilterEgenskapNode(
    node: AdvancedFilterNode | null | undefined
): node is AdvancedFilterEgenskapNode {
    return node?.kind === 'egenskap';
}

export function isAdvancedFilterOverlappNode(
    node: AdvancedFilterNode | null | undefined
): node is AdvancedFilterOverlappNode {
    return node?.kind === 'overlapp';
}

export function isAdvancedFilterRelasjonNode(
    node: AdvancedFilterNode | null | undefined
): node is AdvancedFilterRelasjonNode {
    return node?.kind === 'relasjon';
}

export function operatorRequiresValue(operator: OperatorE | null): boolean {
    return (
        operator !== null &&
        operator !== OperatorE.HAS_VALUE &&
        operator !== OperatorE.NO_VALUE
    );
}

export function advancedFilterValueToHashValue(
    value: EgenskapsfilterVerdi | null
): string | number | null {
    if (value === null || value === undefined) {
        return null;
    }
    if (value instanceof EgenskapTillattVerdi) {
        return value.id;
    }
    return value;
}
